<template>
  <div
    class="chart-scroll-wrapper"
    :style="{ maxHeight: wrapperMaxHeight + 'px' }"
  >
    <div class="chart-inner" :style="{ height: chartHeight + 'px' }">
      <canvas ref="chartCanvas"></canvas>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
import Chart from 'chart.js/auto';

export default {
  name: 'BarChart',
  props: {
    chartData: {
      type: Object,
      required: true
    },
    options: {
      type: Object,
      default: () => ({})
    },
    maxVisibleItems: {
      type: Number,
      default: 10
    },
    rowHeight: {
      type: Number,
      default: 25
    },
    minHeight: {
      type: Number,
      default: 300
    }
  },
  setup(props) {
    const chartCanvas = ref(null);
    let chartInstance = null;

    const itemCount = computed(() => props.chartData?.labels?.length || 0);

    const chartHeight = computed(() => {
      return Math.max(itemCount.value * props.rowHeight, props.minHeight);
    });

    const wrapperMaxHeight = computed(() => {
      return Math.max(props.maxVisibleItems * props.rowHeight, props.minHeight);
    });

    const createChart = () => {
      if (chartInstance) {
        chartInstance.destroy();
      }

      if (chartCanvas.value && props.chartData) {
        const ctx = chartCanvas.value.getContext('2d');

        chartInstance = new Chart(ctx, {
          type: 'bar',
          data: props.chartData,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: 'y',
            plugins: {
              // ADD THIS LINE TO REMOVE THE EXTRA TEXT
              datalabels: { display: false }, 
              
              legend: {
                display: false,
                labels: { color: '#fff' }
              },
              tooltip: {
                rtl: true,
                titleFont: { family: 'Vazir, Arial, sans-serif' },
                bodyFont: { family: 'Vazir, Arial, sans-serif' },
                callbacks: {
                  label: function(context) {
                    return new Intl.NumberFormat('fa-IR').format(context.parsed.x);
                  }
                }
              }
            },
            scales: {
              x: {
                beginAtZero: true,
                grid: { color: 'rgba(255,255,255,0.12)' },
                ticks: {
                  color: '#fff',
                  font: { family: 'Vazir, Arial, sans-serif' },
                  callback: value => new Intl.NumberFormat('fa-IR').format(value)
                }
              },
              y: {
                grid: { color: 'rgba(255,255,255,0.08)' },
                ticks: {
                  autoSkip: false,
                  color: '#fff',
                  // Optional: Add some padding so text doesn't touch the bars
                  padding: 10, 
                  font: { family: 'Vazir, Arial, sans-serif' }
                }
              }
            },
            ...props.options
          }
        });
      }
    };

    onMounted(createChart);

    onUnmounted(() => {
      if (chartInstance) {
        chartInstance.destroy();
      }
    });

    watch(
      () => props.chartData,
      () => {
        createChart();
      },
      { deep: true }
    );

    return {
      chartCanvas,
      chartHeight,
      wrapperMaxHeight
    };
  }
};
</script>

<style scoped>
.chart-scroll-wrapper {
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
}

.chart-inner {
  position: relative;
  width: 100%;
}

.chart-inner canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
}
</style>
